import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Users, 
  ExternalLink,
  Filter,
  Search,
  Loader2
} from 'lucide-react';
import { getText, getCurrentLanguage } from '../services/dataSync';
import { fetchAllEventsPages, DEFAULT_BASE_URL } from '../services/api';
import { getEventStartEnd } from '../utils/eventDateTime';
import toast from 'react-hot-toast';

// Helper function to strip HTML tags from text for previews
const stripHtmlTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const getApiOrigin = () => {
  try {
    const parsed = new URL(DEFAULT_BASE_URL.replace(/\/$/, ''));
    return `${parsed.protocol}//${parsed.host}`;
  } catch {
    return window.location.origin;
  }
};

const getEventImageSrc = (event) => {
  const raw = event?.imageUrl || event?.image || '';
  if (!raw) return '';
  if (raw.startsWith('http') || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
  if (raw.startsWith('/')) return `${getApiOrigin()}${raw}`;
  return raw;
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        const data = await fetchAllEventsPages();
        const safeArray = Array.isArray(data) ? data : [];
        // Filter only published events
        const publishedEvents = safeArray.filter(event => event.isPublished !== false);
        // Sort by date (latest first)
        const sorted = publishedEvents.sort((a, b) => {
          const { start: startA } = getEventStartEnd(a);
          const { start: startB } = getEventStartEnd(b);
          const timeA = startA ? startA.getTime() : 0;
          const timeB = startB ? startB.getTime() : 0;
          return timeB - timeA; // Descending: latest events first
        });
        setEvents(sorted);
        setFilteredEvents(sorted);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to load events:', e);
        }
        toast.error(getText('events.loadingError') || 'Failed to load events. Please try again later.');
        setEvents([]);
        setFilteredEvents([]);
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
    
    const handleDataChange = (event) => {
      if (event.detail && event.detail.type === 'events') {
        loadEvents();
      }
    };
    
    window.addEventListener('dataChanged', handleDataChange);
    
    return () => {
      window.removeEventListener('dataChanged', handleDataChange);
    };
  }, []);

  useEffect(() => {
    let filtered = events;
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(event => event.category === selectedCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(event => 
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    setFilteredEvents(filtered);
  }, [events, selectedCategory, searchTerm]);

  // Get unique categories from events
  const categories = ['all', ...new Set(events.map(event => event.category).filter(Boolean))];

  const formatDate = (dateString) => {
    if (!dateString) return getText('events.dateTBD');
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return getText('events.dateTBD');
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return getText('events.dateTBD');
    }
  };

  const getEventStatus = (event) => {
    try {
      if (event?.status === 'Cancelled') return 'Cancelled';
      if (event?.status === 'Completed') return 'Completed';
      if (event?.status === 'Ongoing') return 'Ongoing';
      if (event?.status === 'Upcoming') return 'Upcoming';

      const now = new Date();
      const { start, end } = getEventStartEnd(event);
      if (!start || !end) return getText('events.status.upcoming');

      if (now < start) return getText('events.status.upcoming');
      if (now >= start && now <= end) return getText('events.status.ongoing');
      return getText('events.status.completed');
    } catch (e) {
      return getText('events.status.upcoming');
    }
  };

  const getStatusColor = (status) => {
    const statusText = status || '';
    if (statusText === 'Cancelled') return 'bg-red-100 text-red-800';
    if (statusText.includes(getText('events.status.upcoming')) || statusText === 'Upcoming') return 'bg-green-100 text-green-800';
    if (statusText.includes(getText('events.status.ongoing')) || statusText === 'Ongoing') return 'bg-yellow-100 text-yellow-800';
    if (statusText.includes(getText('events.status.completed')) || statusText === 'Completed') return 'bg-gray-100 text-gray-800';
    return 'bg-blue-100 text-blue-800';
  };

  const getCategoryTranslation = (category) => {
    const categoryMap = {
      'Workshop': getText('events.category.workshop'),
      'Seminar': getText('events.category.seminar'),
      'Training': getText('events.category.training'),
      'Meeting': getText('events.category.meeting'),
      'Conference': getText('events.category.conference'),
      'Webinar': getText('events.category.webinar'),
      'Exhibition': getText('events.category.exhibition'),
      'General': getText('events.category.general')
    };
    return categoryMap[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Workshop': 'bg-blue-100 text-blue-800',
      'Seminar': 'bg-green-100 text-green-800',
      'Training': 'bg-purple-100 text-purple-800',
      'Meeting': 'bg-orange-100 text-orange-800',
      'Conference': 'bg-red-100 text-red-800',
      'Webinar': 'bg-indigo-100 text-indigo-800',
      'Exhibition': 'bg-pink-100 text-pink-800',
      'General': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              {getText('events.title') || 'Events & Programs'}
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-blue-100 leading-relaxed max-w-3xl mx-auto">
              {getText('events.subtitle') || 'Stay updated with our latest workshops, seminars, and training programs'}
            </p>
          </div>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="bg-white border-b border-gray-200 sticky top-16 z-40 shadow-sm">
        <div className="container-medium py-4 md:py-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-3 w-full md:w-auto">
              <Filter className="h-5 w-5 text-gray-500 flex-shrink-0" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="flex-1 md:flex-none px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? getText('events.allCategories') : getCategoryTranslation(category)}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="relative w-full md:w-auto">
              <Search className="h-5 w-5 text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder={getText('events.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Events + Sidebar */}
      <section className="py-16">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {/* Main Events List */}
            <div className="md:col-span-2 lg:col-span-3">
              {loading ? (
                <div className="text-center py-20">
                  <Loader2 className="h-12 w-12 text-primary-600 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">{getText('events.loading')}</p>
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="text-center py-20">
                  <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{getText('events.noEventsFound')}</h3>
                  <p className="text-gray-500">{getText('events.tryAdjusting')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {filteredEvents.map((event) => {
                const status = getEventStatus(event);
                const eventDate = event.startDate || event.date;
                const startTime = event.startTime || event.time;
                const endTime = event.endTime;
                
                return (
                  <Link
                    to={`/events/${event._id || event.id}`}
                    key={event._id || event.id}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 block group"
                  >
                    {/* Event Image */}
                    <div className="relative h-48 bg-gradient-to-br from-primary-100 to-primary-200 overflow-hidden">
                      {event.imageUrl || event.image ? (
                        <img
                          src={getEventImageSrc(event)}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full flex items-center justify-center ${event.imageUrl || event.image ? 'hidden' : ''}`}>
                        <Calendar className="h-16 w-16 text-primary-600" />
                      </div>
                      
                      {/* Status Badge */}
                      <div className="absolute top-3 right-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(status)}`}>
                          {status}
                        </span>
                      </div>
                    </div>
                    
                    {/* Event Content */}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(event.category)}`}>
                          {event.category ? (getCategoryTranslation(event.category) || event.category) : getText('events.category.general')}
                        </span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {event.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                        {stripHtmlTags(event.description)}
                      </p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="h-4 w-4 mr-2 flex-shrink-0 text-primary-600" />
                          <span>{formatDate(eventDate)}</span>
                        </div>
                        {startTime && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="h-4 w-4 mr-2 flex-shrink-0 text-primary-600" />
                            <span>{startTime}{endTime ? ` - ${endTime}` : ''}</span>
                          </div>
                        )}
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="h-4 w-4 mr-2 flex-shrink-0 text-primary-600" />
                          <span className="truncate">{event.location || getText('events.locationTBD')}</span>
                        </div>
                        {event.maxParticipants && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="h-4 w-4 mr-2 flex-shrink-0 text-primary-600" />
                            <span>{event.currentParticipants || 0}/{event.maxParticipants} {getText('events.participants')}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="text-lg font-bold text-primary-600">
                          {event.fees?.amount === 0 || !event.fees?.amount ? getText('events.free') : `₹${event.fees.amount}`}
                        </div>
                        <div className="flex items-center text-primary-600 text-sm font-semibold group-hover:gap-2 transition-all">
                          {getText('events.viewDetails')}
                          <ExternalLink className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
                </div>
              )}
            </div>

            {/* Sidebar - Recent Events */}
            <aside className="md:col-span-1 lg:col-span-1">
              <div className="bg-white rounded-lg shadow-lg border-2 border-gray-200 p-6">
                <div className="flex items-center gap-4 mb-6 pb-4 border-b border-gray-200">
                  <div className="bg-primary-600 p-2 rounded-lg shadow-sm">
                    <Calendar className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900">{getText('events.recentEvents')}</h2>
                </div>
                {loading ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-6 w-6 text-gray-400 animate-spin mx-auto" />
                  </div>
                ) : events.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 text-sm">
                    {getText('events.noRecentEvents')}
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {events.slice(0, 5).map((event, index) => {
                      const eventDate = event.startDate || event.date;
                      const status = getEventStatus(event);
                      const formattedDate = eventDate 
                        ? (() => {
                            try {
                              const date = new Date(eventDate);
                              return isNaN(date.getTime()) 
                                ? '' 
                                : date.toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric'
                                  });
                            } catch (e) {
                              return '';
                            }
                          })()
                        : '';
                      
                      return (
                        <li key={event._id || event.id}>
                          <Link 
                            to={`/events/${event._id || event.id}`}
                            className="group flex flex-col gap-2 p-3 rounded-lg hover:bg-gradient-to-r hover:from-primary-50 hover:to-transparent transition-all duration-200 border border-transparent hover:border-primary-100"
                          >
                            <div className="flex items-start gap-3">
                              <div className="mt-1 h-2 w-2 rounded-full bg-primary-600 flex-shrink-0 group-hover:bg-primary-700 group-hover:scale-125 transition-all"></div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm text-gray-800 group-hover:text-primary-700 transition-colors line-clamp-2 font-semibold leading-snug mb-1.5">
                                  {event.title}
                                </h3>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {formattedDate && (
                                    <div className="flex items-center gap-1 text-xs text-gray-600">
                                      <Calendar className="h-3 w-3 flex-shrink-0 text-primary-600" />
                                      <span>{formattedDate}</span>
                                    </div>
                                  )}
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                                    {status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>

    </div>
  );
};

export default EventsPage;
